import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { platforms, siteMeta } from "../../mocks/gossip";
import type { PlatformItem } from "../../types";

const NAV = [
  { label: "首页", to: "/" },
  { label: "最新爆料", to: "/#feed" },
  { label: "全网热搜", to: "/#rank" },
  { label: "深扒专题", to: "/#feature" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return;
    const k = keyword.trim();
    setKeyword("");
    setSearchOpen(false);
    navigate(`/channel/weibo?q=${encodeURIComponent(k)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex items-center justify-between gap-4 px-4 md:px-8 h-16 md:h-[72px]">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-600 text-background-50 flex items-center justify-center shadow-sm">
            <i className="ri-flashlight-fill text-lg" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-foreground-900 text-lg md:text-xl tracking-tight whitespace-nowrap">明星爆料</span>
            <span className="text-[11px] text-foreground-500 tracking-widest whitespace-nowrap mt-0.5">{siteMeta.slogan}</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link to="/" className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 rounded-md font-medium whitespace-nowrap">首页</Link>
          <div className="relative" ref={dropRef}>
            <button
              type="button"
              onClick={() => setDropOpen((v) => !v)}
              className={`px-3 py-2 text-sm rounded-md font-medium whitespace-nowrap inline-flex items-center gap-1 cursor-pointer ${
                dropOpen ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
              }`}
              aria-haspopup="true"
              aria-expanded={dropOpen}
            >
              平台频道
              <i className={`ri-arrow-down-s-line text-base transition-transform ${dropOpen ? "rotate-180" : ""}`} aria-hidden="true"></i>
            </button>
            {dropOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2">
                <div className="w-72 rounded-xl bg-background-50 border border-background-200 p-2 grid grid-cols-2 gap-1 shadow-sm">
                  {platforms.map((p: PlatformItem) => (
                    <Link
                      key={p.slug}
                      to={`/channel/${p.slug}`}
                      onClick={() => setDropOpen(false)}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 whitespace-nowrap"
                    >
                      <i className={`${p.icon} text-primary-500 text-base`} aria-hidden="true"></i>
                      {p.short}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {NAV.filter((n) => n.to.startsWith("/#")).map((n) => (
            <Link key={n.to} to={n.to} className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 rounded-md font-medium whitespace-nowrap">
              {n.label}
            </Link>
          ))}
          <Link to="/about" className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 rounded-md font-medium whitespace-nowrap">关于我们</Link>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="w-10 h-10 hidden sm:flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-200 cursor-pointer transition"
            aria-label="搜索"
          >
            <i className="ri-search-line text-xl" aria-hidden="true"></i>
          </button>
          <Link
            to="/#feed"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-primary-600 text-background-50 px-4 py-2 text-sm font-medium hover:bg-primary-700 whitespace-nowrap"
          >
            <i className="ri-goblet-fill text-base" aria-hidden="true"></i> 开始吃瓜
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-200 cursor-pointer"
            aria-label="菜单"
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-2xl`} aria-hidden="true"></i>
          </button>
        </div>
      </nav>

      {searchOpen && (
        <form onSubmit={onSearch} className="mx-auto px-4 md:px-8 pb-3 max-w-2xl">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" aria-hidden="true"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜瓜：输入演员、剧集或事件关键词"
              className="w-full rounded-full bg-background-100 border border-background-200 pl-11 pr-4 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
              aria-label="搜索关键词"
            />
          </div>
        </form>
      )}

      {open && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3 space-y-1">
          <Link to="/" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-800 rounded-md hover:bg-background-100">首页</Link>
          <div className="px-3 pt-2 pb-1">
            <p className="text-xs font-medium text-foreground-400 mb-2 tracking-wider">平台频道</p>
            <div className="grid grid-cols-2 gap-1">
              {platforms.map((p: PlatformItem) => (
                <Link key={p.slug} to={`/channel/${p.slug}`} onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 hover:bg-primary-50">
                  <i className={`${p.icon} text-primary-500`} aria-hidden="true"></i>
                  {p.short}
                </Link>
              ))}
            </div>
          </div>
          <Link to="/#feed" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-800 rounded-md hover:bg-background-100">最新爆料</Link>
          <Link to="/#rank" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-800 rounded-md hover:bg-background-100">全网热搜</Link>
          <Link to="/#feature" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-800 rounded-md hover:bg-background-100">深扒专题</Link>
          <Link to="/about" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-800 rounded-md hover:bg-background-100">关于我们</Link>
        </div>
      )}
    </header>
  );
}